/**
 * Обёртка над Telegram WebApp API.
 *
 * `initDataUnsafe` используем ТОЛЬКО чтобы мгновенно нарисовать имя и тему —
 * это недоверенные данные. Всё, что влияет на игру, идёт через серверную
 * проверку `initData` в /api/auth.
 */

interface HapticFeedback {
  impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
  notificationOccurred(type: "error" | "success" | "warning"): void;
  selectionChanged(): void;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; first_name?: string; username?: string; photo_url?: string };
    start_param?: string;
  };
  colorScheme: "light" | "dark";
  themeParams: Record<string, string>;
  viewportHeight: number;
  viewportStableHeight: number;
  safeAreaInset?: { top: number; bottom: number; left: number; right: number };
  contentSafeAreaInset?: { top: number; bottom: number; left: number; right: number };
  isExpanded: boolean;
  isFullscreen?: boolean;
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  ready(): void;
  expand(): void;
  close(): void;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
  disableVerticalSwipes?(): void;
  openTelegramLink(url: string): void;
  openInvoice?(url: string, cb?: (status: "paid" | "cancelled" | "failed" | "pending") => void): void;
  shareMessage?(msgId: string, cb?: (ok: boolean) => void): void;
  switchInlineQuery?(query: string, types?: string[]): void;
  HapticFeedback: HapticFeedback;
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  MainButton: {
    setText(t: string): void;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    showProgress(leaveActive?: boolean): void;
    hideProgress(): void;
  };
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export const tg: TelegramWebApp | null = window.Telegram?.WebApp ?? null;

/** Запущены ли мы внутри Telegram (а не просто в браузере). */
export const inTelegram = Boolean(tg && tg.initData);

export function initTelegram(): void {
  if (!tg) return;
  tg.ready();
  tg.expand();
  // Свайп вниз внутри игры закрывал бы приложение прямо посреди партии.
  tg.disableVerticalSwipes?.();
  // Полноэкранный режим для всех: доска и стол должны занимать экран целиком.
  // Метод появился в Bot API 8.0 — на старых клиентах его просто нет.
  try {
    tg.requestFullscreen?.();
  } catch {
    /* старый клиент — останемся в обычном режиме */
  }
}

/** Мгновенное имя из недоверенных данных — только для приветствия. */
export function quickName(): string | null {
  return tg?.initDataUnsafe?.user?.first_name ?? null;
}

/** `startapp=m_<matchId>` → id партии, в которую нас позвали. */
export function startMatchId(): string | null {
  const raw = tg?.initDataUnsafe?.start_param ?? new URLSearchParams(location.search).get("tgWebAppStartParam");
  if (!raw) return null;
  return raw.startsWith("m_") ? raw.slice(2) : null;
}

export const haptic = {
  tap: () => tg?.HapticFeedback.impactOccurred("light"),
  move: () => tg?.HapticFeedback.impactOccurred("medium"),
  capture: () => tg?.HapticFeedback.impactOccurred("heavy"),
  select: () => tg?.HapticFeedback.selectionChanged(),
  win: () => tg?.HapticFeedback.notificationOccurred("success"),
  lose: () => tg?.HapticFeedback.notificationOccurred("error"),
  warn: () => tg?.HapticFeedback.notificationOccurred("warning"),
};

/**
 * Отступы под вырез экрана и под собственное управление Telegram.
 *
 * Это ДВА РАЗНЫХ отступа, и их надо складывать:
 *  - `safeAreaInset` — железо: чёлка, Dynamic Island, полоса home;
 *  - `contentSafeAreaInset` — интерфейс Telegram: кнопки «Закрыть», ⌄ и ⋯,
 *    которые в полноэкранном режиме висят поверх нашей страницы.
 *
 * Раньше бралcя только один из них — в полноэкранном режиме наши кнопки
 * заезжали под управление Telegram и становились ненажимаемыми.
 */
export function applySafeArea(): void {
  const set = () => {
    const hardware = tg?.safeAreaInset;
    const chrome = tg?.contentSafeAreaInset;
    const root = document.documentElement;

    const top = (hardware?.top ?? 0) + (chrome?.top ?? 0);
    const bottom = (hardware?.bottom ?? 0) + (chrome?.bottom ?? 0);
    const left = (hardware?.left ?? 0) + (chrome?.left ?? 0);
    const right = (hardware?.right ?? 0) + (chrome?.right ?? 0);

    root.style.setProperty("--safe-top", `${top}px`);
    root.style.setProperty("--safe-bottom", `${bottom}px`);
    root.style.setProperty("--safe-left", `${left}px`);
    root.style.setProperty("--safe-right", `${right}px`);
    // В полноэкранном режиме высота уже во весь экран, поэтому берём её.
    root.style.setProperty(
      "--tg-viewport",
      `${tg?.viewportStableHeight || tg?.viewportHeight || window.innerHeight}px`,
    );
  };

  set();
  tg?.onEvent("viewportChanged", set);
  tg?.onEvent("safeAreaChanged", set);
  tg?.onEvent("contentSafeAreaChanged", set);
  tg?.onEvent("fullscreenChanged", set);
  // Поворот экрана и появление клавиатуры отступы тоже меняют.
  window.addEventListener("resize", set);
  window.addEventListener("orientationchange", set);
}

/**
 * Открыть счёт на оплату звёздами.
 *
 * Обратный вызов сообщает исход, но доверять ему нельзя: права выдаёт сервер
 * по вебхуку от Telegram. Здесь статус нужен только чтобы обновить экран.
 */
export function openInvoice(
  url: string,
  cb: (status: "paid" | "cancelled" | "failed" | "pending") => void,
): void {
  if (tg?.openInvoice) tg.openInvoice(url, cb);
  else window.open(url, "_blank");
}

export function openLink(url: string): void {
  if (tg) tg.openTelegramLink(url);
  else window.open(url, "_blank");
}
